import {
  CloseOutlined,
  ReloadOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { Button, Modal, Spin } from "antd";
import { useAntdMessage } from "hooks/useAntdMessage";
import {
  usePostMSReportWhatsappDraft,
  usePostMSReportWhatsappSend,
} from "queries/meetingSchedule";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import MeetingReportWhatsappPreview from "./MeetingReportWhatsappPreview";
import {
  WA_EMPTY_SLOT,
  WA_MAX_POINTS,
  WA_MESSAGE_MAX_LENGTH,
  measureMessageLength,
  resolveMessageParams,
} from "./meetingReportWaMessage";

/**
 * Poin dari draft AI → slot editor. Jumlah slot selalu WA_MAX_POINTS (sisanya
 * string kosong = tampil "-"). Panjang tiap poin tidak dipotong: server tidak
 * membatasi per poin, hanya panjang total pesan.
 */
const normalizePoints = (raw) => {
  const filled = (Array.isArray(raw) ? raw : [])
    .map((p) => String(p ?? "").trim())
    .filter((p) => p && p !== WA_EMPTY_SLOT)
    .slice(0, WA_MAX_POINTS);
  return Array.from({ length: WA_MAX_POINTS }, (_, i) => filled[i] ?? "");
};

/**
 * Modal pratinjau + kirim ringkasan meeting ke customer via WhatsApp.
 * Dibuka otomatis setelah "Hasil Meeting" tersimpan (status → Selesai).
 * Alur: draft (AI) → sales edit inline → kirim. Tidak bisa ditutup lewat
 * mask/X/Esc; hanya via tombol Batal atau Kirim.
 */
const MeetingReportWhatsappModal = ({
  open,
  scheduleId,
  reportOverride,
  appId,
  onClose,
  onSent,
}) => {
  const { t } = useTranslation();
  const { message } = useAntdMessage();

  const draftMutation = usePostMSReportWhatsappDraft();
  const sendMutation = usePostMSReportWhatsappSend();

  // Status body dikelola lokal (bukan flag mutation) agar tidak menampilkan
  // state basi dari draft sebelumnya saat modal dibuka ulang.
  const [status, setStatus] = useState("loading"); // loading | error | ready
  const [errorMsg, setErrorMsg] = useState("");
  const [errorRetryable, setErrorRetryable] = useState(true);
  const [points, setPoints] = useState(() => normalizePoints([]));
  const [preview, setPreview] = useState({
    greeting: "",
    contactName: "",
    company: "",
    template: "",
  });

  const loadDraft = () => {
    if (scheduleId == null) return;
    setStatus("loading");
    setErrorMsg("");
    draftMutation.mutate(
      { id: scheduleId, report_override: reportOverride || undefined },
      {
        onSuccess: (res) => {
          const body = res?.data;
          if (body && body.success === false) {
            setErrorMsg(body.message || "");
            setErrorRetryable(false);
            setStatus("error");
            return;
          }
          const data = body?.data || {};
          setPoints(normalizePoints(data.points));
          setPreview({
            greeting: data.greeting || "",
            contactName: data.contact_name || "",
            company: data.company || data.contact_name || "",
            template: data.template || "",
          });
          setStatus("ready");
        },
        onError: (err) => {
          const httpStatus = err?.response?.status;
          setErrorMsg(err?.response?.data?.message || "");
          // Retry hanya masuk akal untuk 5xx / gangguan jaringan; 4xx (mis.
          // report kosong / belum DONE) tidak akan membaik dengan retry.
          setErrorRetryable(httpStatus == null || httpStatus >= 500);
          setStatus("error");
        },
      }
    );
  };

  // Ambil draft setiap kali modal dibuka; prime "loading" saat tertutup supaya
  // pembukaan berikutnya tak sempat menampilkan preview/error lama.
  useEffect(() => {
    if (open) {
      setPoints(normalizePoints([]));
      loadDraft();
    } else {
      setStatus("loading");
      setErrorMsg("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scheduleId]);

  // Jumlah slot tetap: slot kosong dikirim sebagai "-" (nilai padding yang sama
  // dipakai backend), sehingga posisi tiap poin di pesan terkirim persis seperti
  // di pratinjau. Kirim tetap diblokir kalau tidak ada satu pun poin nyata.
  const hasFilledPoint = points.some((p) => p.trim());

  // Panjang total pesan dicek di sini supaya user tahu sebelum kirim; tanpa ini
  // batas 1024 baru ketahuan sebagai 422 too_long setelah tombol ditekan.
  // Tanpa template (not_configured) panjang tak bisa dihitung — jangan blokir.
  const messageLength = preview.template
    ? measureMessageLength(
        preview.template,
        resolveMessageParams({
          greeting: preview.greeting,
          contactName: preview.contactName,
          company: preview.company,
        }),
        points
      )
    : null;
  const tooLong = messageLength != null && messageLength > WA_MESSAGE_MAX_LENGTH;

  const canSend =
    status === "ready" &&
    hasFilledPoint &&
    !tooLong &&
    !sendMutation.isPending;

  const handleSend = () => {
    if (!canSend) return;
    sendMutation.mutate(
      {
        id: scheduleId,
        points: points
          .slice(0, WA_MAX_POINTS)
          .map((p) => p.trim() || WA_EMPTY_SLOT),
      },
      {
        onSuccess: (res) => {
          if (res?.data && res.data.success === false) {
            message.error(
              res.data.message || t("meeting_report_wa.send_failed")
            );
            return;
          }
          message.success(t("meeting_report_wa.sent"));
          onSent?.();
          onClose?.();
        },
        onError: (err) => {
          message.error(
            err?.response?.data?.message || t("meeting_report_wa.send_failed")
          );
        },
      }
    );
  };

  let body;
  if (status === "loading") {
    body = (
      <div className="flex flex-col items-center gap-3 py-10 text-neutral-500">
        <Spin />
        <p className="mb-0 text-sm">{t("meeting_report_wa.drafting")}</p>
      </div>
    );
  } else if (status === "error") {
    body = (
      <div className="flex flex-col items-center gap-3 py-10">
        <p className="mb-0 text-center text-sm text-red-500">
          {errorMsg || t("meeting_report_wa.draft_failed")}
        </p>
        {errorRetryable && (
          <Button icon={<ReloadOutlined />} onClick={loadDraft}>
            {t("meeting_report_wa.retry")}
          </Button>
        )}
      </div>
    );
  } else {
    body = (
      <MeetingReportWhatsappPreview
        appId={appId}
        template={preview.template}
        greeting={preview.greeting}
        contactName={preview.contactName}
        company={preview.company}
        points={points}
        onChangePoints={setPoints}
        messageLength={messageLength}
      />
    );
  }

  return (
    <Modal
      open={open}
      title={t("meeting_report_wa.modal_title")}
      closable={false}
      maskClosable={false}
      keyboard={false}
      destroyOnHidden
      width={520}
      footer={
        <div className="flex items-center justify-between">
          <Button
            icon={<CloseOutlined />}
            onClick={onClose}
            disabled={sendMutation.isPending}
          >
            {t("button.cancel")}
          </Button>
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={sendMutation.isPending}
            disabled={!canSend}
            onClick={handleSend}
          >
            {t("meeting_report_wa.send")}
          </Button>
        </div>
      }
    >
      {body}
    </Modal>
  );
};

export default MeetingReportWhatsappModal;
